import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../data/api/mainApi";
import { downloadDxf } from "./millingsPage/DxfPicker";
import ImageCropper from "./millingsPage/ImageCropper";
import DxfRegionSelector, { validRegions } from "./millingsPage/DxfRegionSelector";
import DxfLayouts from "./millingsPage/DxfLayouts";
import BatchSlicer from "./millingsPage/BatchSlicer";

const outputFilesFrom = (data) => {
  if (Array.isArray(data?.files)) return data.files;
  if (Array.isArray(data?.result?.files)) return data.result.files;
  if (data?.dxfKey) return [{ name: data.name || "darabolt-resz", dxfKey: data.dxfKey, excludedCrossing: data.excludedCrossing || 0 }];
  return null;
};

export const dxfNamePrefix = name => (name || "").replace(/\.dxf$/i, "").normalize("NFC").replace(/[^\p{L}\p{N}]/gu, "");
// Keep the legacy block workflow available in code, but hide it from the splitter UI.
const SHOW_DXF_BLOCKS = false;

export default function MillingSplitterPage() {
  const [batchMode, setBatchMode] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [batchOpened, setBatchOpened] = useState(false);
  const [params] = useSearchParams();
  const [pending] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("milling-dxf-job") || "null"); } catch { return null; }
  });
  const [sourceKey, setSourceKey] = useState(pending?.sourceKey || params.get("dxf") || "");
  const [sourceName, setSourceName] = useState(pending?.sourceName || params.get("name") || params.get("dxf") || "");
  const [file, setFile] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [scene, setScene] = useState(null);
  const [regions, setRegions] = useState([]);
  const [selected, setSelected] = useState({});
  const [preview, setPreview] = useState(null);
  const [units, setUnits] = useState(4);
  const [job, setJob] = useState(pending?.job || null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const busy = uploading || !!job;
  useEffect(() => {
    if (job) sessionStorage.setItem("milling-dxf-job", JSON.stringify({ sourceKey, sourceName, job }));
    else sessionStorage.removeItem("milling-dxf-job");
  }, [job, sourceKey, sourceName]);
  useEffect(() => {
    if (!job) return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/millings/dxf/jobs/${job.id}`);
        if (cancelled) return;
        if (data.status === "error") { setError(data.error); setJob(null); }
        else if (data.status === "done") {
          if (job.kind === "inspect") {
            const result = data.result || {};
            const nextBlocks = Array.isArray(result.blocks) ? result.blocks : [];
            setBlocks(nextBlocks); setSelected({}); setPreview(nextBlocks[0] || null);
            setScene(result.scene || null); setRegions([]);
            setUnits([1, 4, 5, 6].includes(result.units) ? result.units : 4);
            if (!result.units) setError("A DXF nem jelöli a mértékegységet. Export előtt ellenőrizd; most milliméter van kiválasztva.");
            if (!nextBlocks.length && !result.scene) setError("Nincs megjeleníthető geometria a DXF-ben.");
          } else {
            const completedFiles = outputFilesFrom(data);
            if (completedFiles?.length) {
              setFiles(completedFiles);
              setMessage(`${completedFiles.length} darabolt DXF elkészült. A fájlok lent letölthetők.`);
            } else {
              setError("A darabolás befejeződött, de a szerver nem adott vissza letölthető DXF-fájlt.");
            }
          }
          setJob(null);
        } else timer = setTimeout(poll, 2000);
      } catch (failure) {
        if ([401, 403, 404].includes(failure.response?.status)) {
          if (!cancelled) { setError("A feldolgozás már nem érhető el. Indítsd újra az elemzést."); setJob(null); }
          return;
        }
        if (!cancelled) { setError("A feldolgozás állapota nem kérdezhető le. Az újrapróbálás ugyanazt a feladatot ellenőrzi."); }
        if (!cancelled) timer = setTimeout(poll, 5000);
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [job]);
  const inspect = async () => {
    setError(""); setMessage(""); setUploading(true); setProgress(0); setFiles([]);
    try {
      let key = sourceKey;
      if (file) {
        const data = new FormData(); data.append("file", file);
        const response = await api.post("/millings/dxf", data, { headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => setProgress(event.total ? Math.round(event.loaded * 100 / event.total) : 0) });
        key = response.data.dxfKey; setSourceKey(key); setFile(null);
      }
      const { data } = await api.post(`/millings/dxf/${encodeURIComponent(key)}/inspect`);
      setJob({ id: data.id, kind: "inspect" }); setBlocks([]); setScene(null); setRegions([]);
    } catch { setError("A DXF feltöltése vagy elemzésének indítása nem sikerült. Lehet, hogy a feldolgozó foglalt."); }
    finally { setUploading(false); }
  };
  const split = async (manual = false) => {
    setError(""); setMessage(""); setUploading(true); setFiles([]);
    try {
      const blocksToSplit = manual ? regions.map((r) => {
        const part = { block: "*MODEL_SPACE", name: r.name.trim(), region: JSON.stringify(r.rect) };
        if (r.cutouts?.length) part.excludeRegions = r.cutouts.map((cutout) => JSON.stringify(cutout.rect));
        return part;
      }) : Object.entries(selected).map(([block, name]) => ({ block, name }));

      const { data } = await api.post(`/millings/dxf/${encodeURIComponent(sourceKey)}/split`, { units, blocks: blocksToSplit });
      const immediateFiles = outputFilesFrom(data);
      if (immediateFiles?.length) {
        setFiles(immediateFiles);
        setMessage(`${immediateFiles.length} darabolt DXF elkészült. A fájlok lent letölthetők.`);
        setJob(null);
      } else if (data?.id) {
        setJob({ id: data.id, kind: "split" });
        setMessage("A darabolás elindult. Az elkészült DXF-ek automatikusan megjelennek lent.");
      } else {
        throw new Error("A szerver nem adott vissza sem feldolgozási azonosítót, sem letölthető DXF-et.");
      }
    } catch (failure) {
      const serverMessage = failure.response?.data?.error || failure.response?.data?.message;
      setError(serverMessage || failure.message || "A darabolás nem indítható. Ellenőrizd a kimeneti neveket; legyenek különbözők.");
      setJob(null);
    } finally { setUploading(false); }
  };
  return <div className="container py-3">
    <div className="d-flex justify-content-between"><h1>Marásminta daraboló</h1><Link to="/millings">Vissza a marásokhoz</Link></div>
    <div className="d-flex gap-2 mb-3">
      <button className={`btn btn-${batchMode ? "outline-primary" : "primary"}`} onClick={() => setBatchMode(false)}>Egyedi darabolás</button>
      <button className={`btn btn-${batchMode ? "primary" : "outline-primary"}`} disabled={busy} onClick={() => { setBatchOpened(true); setBatchMode(true); }}>Több marás darabolása</button>
    </div>
    {batchOpened && <div hidden={!batchMode}><BatchSlicer /></div>}
    <div hidden={batchMode}>
    <section className="card p-3">
      <h2 className="h4">DXF darabolása</h2>
      <p>Jelölj ki részeket kézzel a teljes rajzon, vagy tölts be egy darabolási sablont. A nagy fájlok feldolgozása több percig tarthat.</p>
      <label>DXF kiválasztása (max. 500 MB)<input type="file" accept=".dxf" className="form-control" disabled={busy} onChange={(e) => {
        const next = e.target.files[0]; e.target.value = "";
        if (!next) return;
        if (!next.name.toLowerCase().endsWith(".dxf") || next.size > 500 * 1024 * 1024) { setError("Legfeljebb 500 MB-os DXF-et válassz."); return; }
        setFile(next); setSourceName(next.name); setBlocks([]); setFiles([]); setSelected({}); setPreview(null); setScene(null); setRegions([]); setError(""); setMessage("");
      }} /></label>
      <div className="mt-2">{file?.name || sourceName || (sourceKey ? "Korábban feltöltött DXF kiválasztva" : "")}</div>
      <button className="btn btn-primary align-self-start my-2" disabled={busy || (!file && !sourceKey)} onClick={inspect}>DXF elemzése</button>
      {busy && <p role="status">{uploading ? `Feltöltés / indítás… ${progress}%` : job.kind === "inspect" ? "Blokkok elemzése folyamatban…" : "DXF-ek darabolása folyamatban…"}</p>}
      {error && <p role="alert" className="text-danger">{error}</p>}
      {message && <p role="status" className="text-success">{message}</p>}
      {scene && <>
        <DxfLayouts scene={scene} regions={regions} units={units} onChange={setRegions} disabled={busy} namePrefix={dxfNamePrefix(sourceName)} />
        <DxfRegionSelector scene={scene} regions={regions} onChange={setRegions} disabled={busy} />
        <div className="d-flex gap-3 align-items-end mb-4"><label>Kijelölések mértékegysége<select className="form-select" disabled={busy} value={units} onChange={(e) => setUnits(Number(e.target.value))}>
          <option value={4}>Milliméter</option><option value={5}>Centiméter</option><option value={6}>Méter</option><option value={1}>Hüvelyk</option>
        </select></label><button className="btn btn-primary" disabled={busy || !validRegions(regions)} onClick={() => split(true)}>Kézzel kijelölt részek mentése és DXF készítése</button></div>
        {SHOW_DXF_BLOCKS && <h3 className="h5">Vagy válassz meglévő blokkokat</h3>}
      </>}
      {SHOW_DXF_BLOCKS && blocks.length > 0 && <div className="row">
        <div className="col-lg-7"><div className="table-responsive"><table className="table"><thead><tr><th>Kijelölés</th><th>Blokk / kimeneti név</th><th>Méret (X × Y × Z)</th></tr></thead><tbody>
          {blocks.map((block) => <tr key={block.name}>
            <td><input type="checkbox" aria-label={`${block.name} kijelölése`} disabled={busy} checked={Object.hasOwn(selected, block.name)} onChange={(e) => {
              const next = { ...selected }; if (e.target.checked) next[block.name] = block.name; else delete next[block.name]; setSelected(next);
            }} /></td>
            <td><button className="btn btn-link p-0" onClick={() => setPreview(block)}>{block.name}</button><small className="d-block">{block.uses} hivatkozás</small>
              {Object.hasOwn(selected, block.name) && <input aria-label={`${block.name} kimeneti neve`} className="form-control" maxLength={150} disabled={busy} value={selected[block.name]} onChange={(e) => setSelected({ ...selected, [block.name]: e.target.value })} />}</td>
            <td>{block.max.map((n, i) => (n - block.min[i]).toFixed(1)).join(" × ")}</td>
          </tr>)}
        </tbody></table></div></div>
        <div className="col-lg-5">{preview && <>
          <h3 className="h6">{preview.name} — XY előnézet</h3>
          {preview.lines.length ? <svg role="img" aria-label="DXF drótváz" style={{ width: "100%", height: 320, background: "#f3f5f7" }} viewBox={`${preview.min[0] - 5} ${preview.min[1] - 5} ${Math.max(1, preview.max[0] - preview.min[0]) + 10} ${Math.max(1, preview.max[1] - preview.min[1]) + 10}`}>
            <g transform={`translate(0 ${preview.min[1] + preview.max[1]}) scale(1 -1)`} stroke="#334155" strokeWidth="0.7" fill="none">
              {preview.lines.map(([x1, y1, x2, y2], i) => <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />)}
            </g></svg> : <p>Ehhez az összeállításhoz nincs egyszerű vonalas előnézet. A blokk exportálható.</p>}
          <p className="text-muted">Ritkított előnézet; az export minden geometriai elemet megtart.</p>
        </>}</div>
        <div className="d-flex gap-3 align-items-end"><label>Forrás mértékegysége<select className="form-select" disabled={busy} value={units} onChange={(e) => setUnits(Number(e.target.value))}>
          <option value={4}>Milliméter</option><option value={5}>Centiméter</option><option value={6}>Méter</option><option value={1}>Hüvelyk</option>
        </select></label><button className="btn btn-primary" disabled={busy || !Object.keys(selected).length || Object.values(selected).some((v) => !v.trim())} onClick={() => split(false)}>Kijelölt blokkok darabolása</button></div>
      </div>}
      {files.length > 0 && <div className="mt-3"><h3 className="h5">Elkészült DXF-fájlok</h3>
        <button type="button" className="btn btn-primary mb-2" disabled={downloadingAll || !files.some(f => f.dxfKey)} onClick={async () => {
          setDownloadingAll(true); setError("");
          const failed = [];
          try {
            for (const output of files.filter(f => f.dxfKey)) {
              try { await downloadDxf(output.dxfKey, output.name); } catch { failed.push(output.name); }
              await new Promise(resolve => setTimeout(resolve, 300));
            }
            if (failed.length) setError(`Nem sikerült letölteni: ${failed.join(", ")}`);
          } finally { setDownloadingAll(false); }
        }}>{downloadingAll ? "Letöltések indítása…" : "Összes DXF letöltése külön fájlként"}</button>
        <small className="d-block text-muted">Ha a böngésző rákérdez, engedélyezd a több fájl letöltését.</small>
        <p>Töltsd le a részeket, majd a marásszerkesztőben töltsd fel a megfelelő mintához vagy változathoz.</p>
        {files.map((output, index) => <div key={output.dxfKey || `${output.name}-${index}`} className="mb-2">
          {output.dxfKey ? <button type="button" className="btn btn-outline-primary me-2 mb-2" onClick={async () => {
            setError("");
            try { await downloadDxf(output.dxfKey, output.name); } catch { setError("A letöltés nem sikerült."); }
          }}>{output.name || `Rész-${index + 1}`}.dxf letöltése</button>
            : <p role="alert" className="text-danger">{output.name || `Rész-${index + 1}`}: a szerver nem adott vissza dxfKey-t, ezért ez a fájl nem tölthető le.</p>}
          {output.excludedCrossing > 0 && <p className="text-warning">{output.excludedCrossing} határon átnyúló elem kimaradt ebből a részből. Szükség esetén bővítsd a keretet és mentsd újra.</p>}
        </div>)}
      </div>}
    </section>
    <ImageCropper />
    </div>
  </div>;
}
